import { gql, useSubscription, useMutation, useQuery } from "@apollo/client";
import { StorageHelper } from "../utils/storageHelper";
import { client } from "../apolloClient";

/**
 * useNotification custom hook for notification operations.
 * - Provides real-time notification subscription, fetching, and marking as read.
 * - Handles cache updates for notifications.
 * - Exposes helpers for reading/writing notifications in Apollo cache.
 * - Returns methods for subscribing, fetching, marking as read, and cache helpers.
 */

// GraphQL Queries, Mutations, and Subscriptions
const SUB_NOTIFICATION = gql`
  subscription Subscription(
    $user_id: String!
    $hostname: String!
  ) {
    PushNotification(
      user_id: $user_id
      hostname: $hostname
    ) {
      notification_id
      message
      is_read
      created_on
      action_link
      user_id
      notification_type
    }
  }
`;

const GET_NOTIFICATIONS = gql`
  query GetNotificationsByUserId($userId: ID!) {
    GetNotificationsByUserId(userId: $userId) {
      notifications {
        notification_id
        message
        is_read
        created_on
        action_link
        user_id
        notification_type
      }
    }
  }
`;

const READ_NOTIFICATIONS = gql`
  mutation ReadNotificationByNotificationIds($notificationIds: [ID!]!) {
    ReadNotificationByNotificationIds(notification_ids: $notificationIds) {
      notification_id
      message
      is_read
      created_on
      action_link
      user_id
      hostname
      notification_type
    }
  }
`;

// Cache operations for notifications
const cacheOperations = {
  /**
   * Reads notifications from Apollo cache for a given userId.
   * @param {string|number} userId
   * @returns {Array} Array of notification objects or empty array if not found.
   */
  readNotifications: (userId) => {
    try {
      const { GetNotificationsByUserId = { notifications: [] } } =
        client.readQuery({
          variables: { userId: parseInt(userId) },
          query: GET_NOTIFICATIONS,
        }) || {};
      return GetNotificationsByUserId.notifications;
    } catch {
      return [];
    }
  },
  /**
   * Writes notifications to Apollo cache for a given userId.
   * @param {Array} notifications
   * @param {string|number} userId
   */
  writeNotifications: (notifications, userId) => {
    client.writeQuery({
      variables: { userId: parseInt(userId) },
      query: GET_NOTIFICATIONS,
      data: { GetNotificationsByUserId: { notifications } },
    });
  },
};

export const useNotification = () => {
  // Real-time notification subscription
  const useNotify = useSubscription(SUB_NOTIFICATION, {
    variables: {
      user_id: StorageHelper.get("userDetails")?.user_id.toString(),
      hostname: window.location.hostname,
    },
  });

  // Query for fetching notifications
  const { data: notificationsData, loading: notificationsLoading, refetch: refetchNotifications } = useQuery(GET_NOTIFICATIONS, {
    variables: {
      userId: parseInt(StorageHelper.get("userDetails")?.user_id.toString()),
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  // Mutation for marking notifications as read
  const [readNotifications] = useMutation(READ_NOTIFICATIONS, {
    update(cache, { data: { ReadNotificationByNotificationIds } }) {
      try {
        const userId = parseInt(StorageHelper.get("userDetails")?.user_id.toString());
        const existingData = cache.readQuery({
          query: GET_NOTIFICATIONS,
          variables: { userId },
        });

        if (existingData) {
          const newNotifications = existingData.GetNotificationsByUserId.notifications.map(notification => {
            if (ReadNotificationByNotificationIds.some(read => read.notification_id === notification.notification_id)) {
              return { ...notification, is_read: true };
            }
            return notification;
          });

          cache.writeQuery({
            query: GET_NOTIFICATIONS,
            variables: { userId },
            data: {
              GetNotificationsByUserId: {
                notifications: newNotifications,
              },
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after marking notifications as read:", error);
      }
    },
  });

  /**
   * Marks notifications as read by their IDs.
   * @param {Array} notificationIds
   */
  const markAsRead = async (notificationIds) => {
    try {
      await readNotifications({
        variables: {
          notificationIds: notificationIds.map(id => parseInt(id))
        }
      });
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  return { 
    useNotify, 
    notificationsData, 
    notificationsLoading, 
    markAsRead,
    refetchNotifications,
    cacheOperations
  };
};
